import * as React from 'react';
import { useNavigate } from 'react-router';

import BhanzuWhiteLogo from '@/assets/logos/bhanzu-white-logo.svg';
import { Avatar } from '@/components/ui/avatar';
import { MenuTrigger } from '@/components/ui/menu';
import { env } from '@/config/env';
import { paths } from '@/config/paths';
import { useProfilesData } from '@/features/user/api/queries';
import {
  AvatarLegacyKey,
  avatarMapLegacy,
} from '@/features/user/components/profile-avatar/avatar-map-legacy';
import { VariantDropdown } from '@/layouts/main-layout/components/variant-dropdown';
import { cn } from '@/utils';

import { NotificationUI } from '../navigation/notification-ui';

import UserMenu from './user-menu';
import { getCreditText } from './utils';

export type MobileHeaderProps = React.HTMLAttributes<HTMLElement>;

const MobileHeader = React.forwardRef<HTMLElement, MobileHeaderProps>(
  ({ className, ...props }, _ref) => {
    const navigate = useNavigate();
    const { enrolledCourses, selectedProfile, selectedCourse } =
      useProfilesData();

    const handleLogoClick = async () => {
      await navigate(paths.app.root.getHref());
    };
    let avatarLeftText = '';

    if (env.IS_OFFLINE) {
      if (selectedProfile?.absolute_grade) {
        avatarLeftText = `Level ${selectedProfile?.absolute_grade}`;
      }
    } else {
      avatarLeftText = getCreditText(selectedCourse);
    }

    const avatar = selectedProfile?.avatar ?? 'bird0001';
    // Check if avatar exists in avatarMapLegacy, otherwise use default
    const avatarKey = (
      avatar in avatarMapLegacy ? avatar : 'bird0001'
    ) as AvatarLegacyKey;

    return (
      <header
        className={cn(
          'w-full bg-magical-moonlight-d3 rounded-b-3xl fixed top-0 z-(--z-docked) left-0 right-0',
          className
        )}
        {...props}
      >
        <div className="max-w-(--max-width) mx-auto px-4 py-4">
          {/* Top: Centered Logo */}
          <div className="flex justify-center mb-4">
            <button onClick={() => void handleLogoClick()}>
              <img alt="Bhanzu logo" src={BhanzuWhiteLogo} className="h-8" />
            </button>
          </div>

          {/* Bottom: Avatar, Course Selector, Notification */}
          <div className="flex items-center justify-between gap-3">
            {/* Left: Avatar with Credits */}
            <MenuTrigger menu={<UserMenu />} align="start">
              <div className="flex flex-col items-center gap-1 cursor-pointer">
                <Avatar
                  icon={avatarMapLegacy[avatarKey]}
                  size="md"
                  className="w-14 h-14 border-2 border-magical-moonlight-d1"
                />
                {avatarLeftText ? (
                  <span className="text-xs font-medium text-white whitespace-nowrap">
                    {avatarLeftText}
                  </span>
                ) : null}
              </div>
            </MenuTrigger>

            {/* Center: Course Selector */}
            {enrolledCourses && enrolledCourses.length > 1 ? (
              <div className="flex-1 max-w-[200px]">
                <VariantDropdown
                  size="mobile"
                  variant="mobile"
                  className="w-full"
                />
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center px-4 py-2 bg-magical-moonlight-d2 border border-magical-moonlight-d1 rounded-2xl">
                <span className="text-sm font-medium text-white truncate">
                  {selectedCourse?.course_name ?? 'Course'}
                </span>
              </div>
            )}

            {/* Right: Notification Icon */}
            <NotificationUI />
          </div>
        </div>
      </header>
    );
  }
);

MobileHeader.displayName = 'MobileHeader';

export { MobileHeader };
